import { Copy, Eye, Link2, Lock, Search, X } from "lucide-react-native";
import { FC, useMemo, useState } from "react";

import {
  Box,
  DropdownPicker,
  HStack,
  Input,
  InputField,
  InputIcon,
  InputSlot,
  Text,
  VStack,
} from "@/components/ui";
import { useLocalization } from "@/hooks";
import { usePanels } from "@/hooks/api";
import { PanelAssignMode, SharePermission } from "@/types/resources";

import { ChoiceCard, Field, SectionTitle, Segmented } from "./form";
import PanelCard, { PanelCardSkeleton } from "./PanelCard";

type OrgAccess = SharePermission | "restricted";

export interface PanelAssignDraft {
  defaultPanelId: string | null;
  mode: PanelAssignMode;
  orgPermission: SharePermission | null;
  ownerId: string | null;
  panelIds: string[];
}

export const EMPTY_PANEL_ASSIGN: PanelAssignDraft = {
  defaultPanelId: null,
  mode: "copy",
  orgPermission: "view",
  ownerId: null,
  panelIds: [],
};

const PanelAssignFields: FC<{
  onChange: (value: PanelAssignDraft) => void;
  owners: { label: string; value: string }[] | null;
  showOrgAccess: boolean;
  value: PanelAssignDraft;
}> = ({ onChange, owners, showOrgAccess, value }) => {
  const { i18n } = useLocalization();
  const { loading, panels: library } = usePanels();
  const [query, setQuery] = useState("");
  const [queryKey, setQueryKey] = useState(0);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q
      ? library.filter((p) => p.name.toLowerCase().includes(q))
      : library;
  }, [library, query]);

  const defaultItems = useMemo(
    () => [
      { label: i18n.t("forms.panels.defaultNone"), value: "none" },
      ...library
        .filter((p) => value.panelIds.includes(p.id))
        .map((p) => ({ label: p.name, value: p.id })),
    ],
    [i18n, library, value.panelIds]
  );

  const toggle = (panelId: string) => {
    const panelIds = value.panelIds.includes(panelId)
      ? value.panelIds.filter((id) => id !== panelId)
      : [...value.panelIds, panelId];
    onChange({
      ...value,
      defaultPanelId: panelIds.includes(value.defaultPanelId ?? "")
        ? value.defaultPanelId
        : null,
      panelIds,
    });
  };

  return (
    <VStack className="gap-5">
      <VStack className="gap-3">
        <HStack className="items-end justify-between">
          <SectionTitle
            subtitle={i18n.t("forms.panels.librarySubtitle")}
            title={i18n.t("forms.panels.library")}
          />
          <Text className="text-xs font-medium text-violet-600">
            {i18n.t("forms.panels.selectedCount", {
              count: value.panelIds.length,
            })}
          </Text>
        </HStack>
        {loading ? (
          <HStack className="flex-wrap" style={{ margin: -6 }}>
            {[0, 1, 2].map((i) => (
              <Box className="w-1/3 p-1.5" key={i}>
                <PanelCardSkeleton />
              </Box>
            ))}
          </HStack>
        ) : library.length === 0 ? (
          <Text className="text-sm text-typography-400 py-4">
            {i18n.t("forms.panels.libraryEmpty")}
          </Text>
        ) : (
          <>
            <Input className="bg-white border-outline-200 rounded-xl" size="md">
              <InputSlot className="pl-3">
                <InputIcon as={Search} className="text-typography-400" />
              </InputSlot>
              <InputField
                className="text-sm"
                defaultValue={query}
                key={queryKey}
                onChangeText={setQuery}
                placeholder={i18n.t("forms.panels.searchPlaceholder")}
              />
              {query.length > 0 && (
                <InputSlot
                  className="pr-3"
                  onPress={() => {
                    setQuery("");
                    setQueryKey((k) => k + 1);
                  }}
                >
                  <InputIcon as={X} className="text-typography-400" />
                </InputSlot>
              )}
            </Input>
            {filtered.length === 0 ? (
              <Text className="text-sm text-typography-400 py-4">
                {i18n.t("forms.panels.noMatches")}
              </Text>
            ) : (
              <HStack className="flex-wrap" style={{ margin: -6 }}>
                {filtered.map((panel) => (
                  <Box className="w-1/3 p-1.5" key={panel.id}>
                    <PanelCard
                      applied={value.panelIds.includes(panel.id)}
                      onPress={() => toggle(panel.id)}
                      panel={panel}
                    />
                  </Box>
                ))}
              </HStack>
            )}
          </>
        )}
      </VStack>

      {value.panelIds.length > 0 && (
        <>
          <Field label={i18n.t("forms.panels.mode")}>
            <HStack className="gap-3 flex-wrap">
              <ChoiceCard
                description={i18n.t("forms.panels.copyDescription")}
                icon={Copy}
                onPress={() => onChange({ ...value, mode: "copy" })}
                selected={value.mode === "copy"}
                title={i18n.t("forms.panels.copy")}
              />
              <ChoiceCard
                description={i18n.t("forms.panels.shareDescription")}
                icon={Link2}
                onPress={() =>
                  onChange({ ...value, defaultPanelId: null, mode: "share" })
                }
                selected={value.mode === "share"}
                title={i18n.t("forms.panels.share")}
              />
            </HStack>
          </Field>

          <HStack className="gap-3 flex-wrap">
            {value.mode === "copy" && owners && (
              <Field
                hint={i18n.t("forms.panels.ownerHint")}
                label={i18n.t("forms.panels.owner")}
              >
                <DropdownPicker
                  items={owners}
                  onChange={(ownerId) => onChange({ ...value, ownerId })}
                  placeholder={i18n.t("forms.panels.ownerPlaceholder")}
                  value={value.ownerId}
                />
              </Field>
            )}
            {value.mode === "copy" && (
              <Field
                hint={i18n.t("forms.panels.defaultHint")}
                label={i18n.t("forms.panels.default")}
              >
                <DropdownPicker
                  items={defaultItems}
                  onChange={(id) =>
                    onChange({
                      ...value,
                      defaultPanelId: id === "none" ? null : id,
                    })
                  }
                  placeholder={i18n.t("forms.panels.defaultNone")}
                  value={value.defaultPanelId ?? "none"}
                />
              </Field>
            )}
          </HStack>

          {showOrgAccess && (
            <Field
              hint={i18n.t("forms.panels.orgAccessHint")}
              label={i18n.t("forms.panels.orgAccess")}
            >
              <Segmented<OrgAccess>
                onChange={(access) =>
                  onChange({
                    ...value,
                    orgPermission: access === "restricted" ? null : access,
                  })
                }
                options={[
                  {
                    icon: Lock,
                    label: i18n.t("forms.panels.restricted"),
                    value: "restricted",
                  },
                  {
                    icon: Eye,
                    label: i18n.t("forms.panels.view"),
                    value: "view",
                  },
                ]}
                value={value.orgPermission ?? "restricted"}
              />
            </Field>
          )}
        </>
      )}
    </VStack>
  );
};

export default PanelAssignFields;
